// ============================================================================
//  help.js — the content behind /help.
//
//  Sections are plain data so the copy can be edited without touching JSX.
//  `minRank` gates visibility: 1 = every man, 2 = Team Admin and up, and so on
//  up the ladder. Help.jsx filters on the viewer's rank and renders in order.
//
//  Keep this honest. Every step below describes what the app actually does
//  today - if you change the UI, change the step.
// ============================================================================

/** Block types: para | steps | list | note | example */

export const HELP_SECTIONS = [
  /* ---------------------------------------------------------------- */
  {
    id: "getting-started",
    minRank: 1,
    title: "Getting started",
    kicker: "The rhythm",
    intro:
      "Mile Marker holds the game plan you built at the retreat: two to three SMART goals under each of the five F's, checked once a month with your group.",
    blocks: [
      {
        type: "list",
        title: "The five F's",
        items: [
          ["Faith", "The foundation. It starts with God."],
          ["Family", "Lead at home before anywhere else."],
          ["Friends", "Don't do life alone."],
          ["Fitness", "Steward the body you were given."],
          ["Finances", "Manage what you've been entrusted."],
        ],
      },
      {
        type: "steps",
        title: "Writing a goal",
        steps: [
          "On My Dashboard, tap Add SMART goal.",
          "Pick the F it belongs to and give it a headline - short, the way you would say it out loud.",
          "Fill in the five SMART fields. Specific, Measurable, Achievable, Relevant, Time-bound.",
          "Not sure it is sharp enough? Use the Coach (next section). Then Save.",
        ],
      },
      {
        type: "steps",
        title: "Logging a Mile Marker",
        steps: [
          "Once a month, before your group meets, open a goal and tap Log Mile Marker.",
          "Set the percentage and pick On Track, At Risk, or Off Track. Be honest - amber and red are what the meeting is for.",
          "Write the status note, anything blocking you, and the one commitment you are making for next month.",
          "Add a progress photo if the goal has visible proof: a scale, a screenshot, a finish line.",
        ],
      },
      {
        type: "note",
        tone: "plain",
        text:
          "One Mile Marker per goal per month. Logging again in the same month updates that month rather than adding a second entry.",
      },
    ],
  },

  /* ---------------------------------------------------------------- */
  {
    id: "coach",
    minRank: 1,
    title: "Using the SMART Goal Coach",
    kicker: "Your sharpening tool",
    intro:
      "The Coach is an AI trained on the 5F rhythm. Its job is to help you turn a vague intention into a goal you can actually measure. It is an advisor, not a gatekeeper - you can always save your goal regardless of what it says.",
    blocks: [
      {
        type: "steps",
        title: "How to reach it",
        steps: [
          "The Coach lives inside the goal editor, not on the goal card. Tap Add SMART goal for a new one, or the pencil icon on a goal you already have.",
          "Give the goal a headline first. The Coach button stays locked and reads \"Add a headline first to unlock the Coach\" until you do.",
          "Then tap Get Coach Feedback at the bottom of the editor.",
        ],
      },
      {
        type: "note",
        tone: "key",
        title: "Fill the SMART fields in before you open it",
        text:
          "The Coach reads your draft the instant the panel opens and immediately gives its first response. Whatever you have typed is what it sees; empty fields reach it as blank. Rough, honest words beat empty boxes - you will get far better coaching from a messy first attempt than from a blank form.",
      },
      {
        type: "steps",
        title: "Working with it",
        steps: [
          "Read the opening response. It will tell you what is already strong, then name the SMART letter that needs the most work.",
          "Talk back. Type into the box at the bottom and press send. Ask \"what would you tighten first?\" or push back if it missed your context. It is a conversation, not a verdict.",
          "If it hands you a better sentence, paste it into the matching field.",
          "Tap Save on the goal when you are done.",
        ],
      },
      {
        type: "note",
        tone: "warn",
        title: "The conversation is not saved",
        text:
          "Close the panel and the chat is gone for good. Only what you typed into the goal fields and saved is kept. If the Coach gives you wording you like, put it in the field before you close.",
      },
      {
        type: "example",
        title: "What sharpening looks like",
        before: {
          label: "Before",
          title: "Get in better shape",
          note: "No number and no date. There is nothing here to check in on next month.",
        },
        after: {
          label: "After",
          title: "Run a 10K in under an hour",
          note:
            "Measurable is now a distance and a clock. Put the race date in Time-bound and this becomes a yes or no at your Mile Marker meeting, not a feeling.",
        },
      },
      {
        type: "list",
        title: "What the Coach can and cannot see",
        items: [
          ["Sees", "The single goal draft open on your screen, and a short list of teaching videos it can point you to."],
          ["Does not see", "Your other goals, your Mile Marker history, your notes or blockers, or anything belonging to another man."],
        ],
      },
      {
        type: "list",
        title: "Good to know",
        items: [
          ["Videos", "It suggests a teaching video only when one genuinely fits the gap - at most one per reply. Not getting one is not a failure."],
          ["Length", "Replies are short by design. It is built to be read on a phone."],
          ["\"Coach unreachable\"", "The connection dropped. Save your goal first, then try again in a moment. Nothing you typed is lost."],
        ],
      },
    ],
  },

  /* ---------------------------------------------------------------- */
  {
    id: "privacy",
    minRank: 1,
    title: "What your team can see",
    kicker: "Read this one",
    intro:
      "Mile Marker is built for brothers who hold each other accountable. That only works if everyone knows exactly who sees what. No surprises.",
    blocks: [
      {
        type: "list",
        title: "By default",
        items: [
          ["Your teammates", "Every man on your team sees your goals, your percentages, your status, and your monthly notes - including what you write in Blockers - plus any progress photos you add."],
          ["Your Team Admin", "The same, for every man on the team."],
          ["Other teams", "Men on other teams cannot see you at all."],
        ],
      },
      {
        type: "note",
        tone: "plain",
        text:
          "Write your Blockers as though a brother will read them, because he will. That is the point - a struggle named out loud is a struggle the group can walk into with you.",
      },
      {
        type: "steps",
        title: "Keeping one goal's updates private",
        steps: [
          "Some things are not ready to be shared with the whole group yet. That is fine, and you do not have to leave them out of your game plan.",
          "Open the goal (tap the pencil icon), tick Keep my updates private at the bottom, and Save.",
          "Your monthly notes, blockers and photos on that goal are now hidden from your teammates. Your Team Admin still sees them, so you are never carrying it alone.",
          "The goal itself still shows on your card with its percentage and status, and it carries a Private tag. The group can still ask how it is going - they just do not read the details.",
        ],
      },
      {
        type: "note",
        tone: "key",
        title: "Turn it off whenever you are ready",
        text:
          "Private is not permanent. Untick it and Save, and your updates on that goal are shared with the team again. Plenty of men keep a goal private for a season and then open it up once they have some ground under them.",
      },
      {
        type: "note",
        tone: "key",
        title: "Only you can change your own goals",
        text:
          "Everyone else on your team has read access. Nobody - not your Team Admin, not an Admin - can edit your goals, change your percentages, or log a Mile Marker on your behalf. Your game plan stays yours.",
      },
      {
        type: "note",
        tone: "warn",
        text:
          "If something is too heavy for the page even with Private on, bring it to your Team Admin or your pastor in person. This app is a tool for the walk, not a replacement for it.",
      },
    ],
  },

  /* ---------------------------------------------------------------- */
  {
    id: "team-admin",
    minRank: 2,
    title: "Running a team",
    kicker: "For Team Admins",
    intro:
      "You are the Team Admin for your group. You keep the roster right and make sure every man is actually logging.",
    blocks: [
      {
        type: "list",
        title: "What you can do",
        items: [
          ["Invite men", "Add a new man to your team. He gets an email invite and sets his own password."],
          ["Suspend and restore", "Suspend a man who has stepped away. He keeps his data and can be restored any time."],
          ["Rename your team", "Change what your cohort is called."],
          ["See everything", "Every goal, note, blocker, and photo for every man on your team."],
        ],
      },
      {
        type: "note",
        tone: "plain",
        text:
          "A team can have more than one Team Admin. If you co-lead a cohort, both of you get the full picture.",
      },
      {
        type: "note",
        tone: "key",
        title: "Private goals still come to you",
        text:
          "A man can mark a goal's updates private, which hides his notes, blockers and photos on it from his teammates. It does not hide them from you. When you see a Private tag, that man has chosen to bring this one to his leader rather than the whole room. Treat it that way: follow up one on one, and do not read it out in the meeting.",
      },
      {
        type: "list",
        title: "What you cannot do",
        items: [
          ["Edit a man's goals", "Read-only, always. Coach him in the meeting instead."],
          ["Create a team", "Ask an Admin to spin up a new cohort."],
          ["Change roles", "Promoting a man to Team Admin is an Admin's call."],
        ],
      },
      {
        type: "steps",
        title: "Running the monthly meeting",
        steps: [
          "Open Team before the meeting and scan the roster for red and amber.",
          "Tap into each man to read his notes and blockers from the last month.",
          "Start with what moved. Then work the blockers as a group.",
          "Close by making sure every man has logged and named next month's commitment.",
        ],
      },
    ],
  },

  /* ---------------------------------------------------------------- */
  {
    id: "admin",
    minRank: 3,
    title: "Running multiple teams",
    kicker: "For Admins",
    intro:
      "You oversee more than one cohort. Everything a Team Admin can do, across every team assigned to you.",
    blocks: [
      {
        type: "list",
        title: "What you add",
        items: [
          ["Multiple teams", "Your Team view is grouped by cohort. You see every team you have been assigned, and only those."],
          ["Assign Team Admins", "Promote a man to Team Admin and put him over one of your teams."],
          ["Move men", "Shift a man from one of your teams to another."],
          ["Invite Team Admins", "Not just members."],
        ],
      },
      {
        type: "note",
        tone: "plain",
        text:
          "You can only ever act on teams assigned to you, and only on men below your own level. Creating a brand new cohort is a Super Admin's call.",
      },
    ],
  },

  /* ---------------------------------------------------------------- */
  {
    id: "super-admin",
    minRank: 4,
    title: "Everything",
    kicker: "For the Super Admin",
    intro: "You see every team and every man, and you decide who leads what.",
    blocks: [
      {
        type: "list",
        title: "Yours alone",
        items: [
          ["Create teams", "Spin up a new cohort for the next retreat."],
          ["Promote Admins", "Put a man over multiple teams."],
          ["Unassigned", "A bucket on the Team page for men who belong to no cohort yet. Nobody else can see them, so do not let them pile up."],
          ["Bulk invite", "Paste a whole cohort at once from Manage."],
        ],
      },
      {
        type: "note",
        tone: "warn",
        title: "Making another Super Admin",
        text:
          "Deliberately not possible from inside the app. It has to be done directly in the database, so it can never happen by a mis-tap.",
      },
    ],
  },
];
